import React, { useEffect } from "react";
import { useAlertHelpers } from "../../helper/Hooks/useAlertHelpers";
import { useAuth } from "../../contexts/AuthContext";
import { useSession } from "../../contexts/SessionContext";
import { ILoginData } from "../../types/authTypes";
import { useNavigate } from "react-router";
import { useLoginState } from "../login/useLoginState";
import { clinicianLoginConfig } from "../login/loginConfigs";
import SharedLogin from "../login/sharedLogin";

interface LoginClinicianProps {
  title: string;
}

/**
 * function component of logining clinician
 * - use `useLoginState` hook to manage state
 * - use `SharedLogin` component to render UI
 * - include only clinician-sepecific business logic
 */
const LoginClinician = ({ title }: LoginClinicianProps) => {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const { login: sessionLogin } = useSession();

  const { showAlertModal, reportNoClinicSelected } = useAlertHelpers();

  const loginState = useLoginState(clinicianLoginConfig);

  /**Set the page title*/
  useEffect(() => {
    document.title = title;
  }, [title]);

  /**
   * Method for handle the login.
   * The input data are stringified to JSON.
   * Then send JSON to backend API and handle the response.
   */
  const handleLogin = async (event) => {
    event.preventDefault();

    if (!loginState.username) {
      console.warn("Kein Username");
      showAlertModal("Kein Nutzername angegeben.");
      return;
    }
    if (!loginState.password) {
      console.warn("Kein Passwort");
      showAlertModal("Kein Passwort angegeben.");
      return;
    }

    if (!loginState.clinicId || loginState.clinicId === "") {
      console.warn("Keine Klinik");
      reportNoClinicSelected();
      return;
    }

    const loginData: ILoginData = {
      clinicName: loginState.clinicname,
      clinicId: loginState.clinicId,
      username: loginState.username,
      password: loginState.password,
      role: "clinician",
      userType: "clinician",
    };

    if (!authLogin) {
      console.error("Login function is not available");
      showAlertModal("Anmeldefunktion ist nicht verfügbar.");
      return;
    }

    if (typeof authLogin === "function") {
      await authLogin(loginData, (authResponse: string) => {
        if (authResponse === "success") {
          sessionLogin();
          navigate("/clinician");
        } else if (authResponse === "credentials_wrong") {
          showAlertModal(
            "Ihre Anmeldedaten sind fehlerhaft oder Ihr Account wurde noch nicht freigeschaltet.",
          );
        } else if (authResponse === "no_permissions") {
          showAlertModal("Sie haben für diese Seite keine Berechtigung.");
        } else {
          showAlertModal(
            "Irgendetwas ist schief gelaufen. Bitte versuchen Sie es erneut.",
          );
        }
      });
    }
  };

  return (
    <SharedLogin
      config={clinicianLoginConfig}
      clinics={loginState.clinics}
      onClinicIdChange={loginState.handleClinicId}
      onUsernameChange={loginState.handleUsername}
      onPasswordChange={loginState.handlePassword}
      onSubmit={handleLogin}
    />
  );
};

export default LoginClinician;
